<script setup>
import { computed } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
} from 'chart.js';

const props = defineProps({
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  series: {
    type: Array,
    required: true,
    // Each: { name, color, data: [{ label, value }] }
  },
  height: { type: Number, default: 260 },
  // GK-HUB: Goal line props for FCR meta target
  goalLine: { type: Number, default: null },
  goalLabel: { type: String, default: '' },
  // GK-HUB: Option to hide legend (e.g. FCR evolution only has 1 series + goal)
  showLegend: { type: Boolean, default: true },
});

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler
);

function hexToRgba(hex, alpha) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const fullHex = hex.replace(
    shorthandRegex,
    (_, r, g, b) => r + r + g + g + b + b
  );
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  if (!result) return `rgba(93, 173, 226, ${alpha})`;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function createGradientPlaceholder(colorStart) {
  // Placeholder — real gradient is applied by the plugin
  return colorStart;
}

const chartData = computed(() => {
  if (!props.series.length) return { labels: [], datasets: [] };

  const labels = props.series[0].data.map(d => d.label);

  const datasets = props.series.map(s => {
    // Create gradient fill color (transparent version of line color)
    const fillColor = hexToRgba(s.color, 0.08);
    const fillColorEnd = hexToRgba(s.color, 0.01);

    return {
      label: s.name,
      data: s.data.map(d => d.value),
      borderColor: s.color,
      backgroundColor: createGradientPlaceholder(fillColor),
      pointBackgroundColor: s.color,
      pointBorderColor: '#1e1e2e',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 7,
      pointHoverBorderWidth: 2,
      pointHoverBackgroundColor: s.color,
      borderWidth: 2.5,
      tension: 0.4,
      fill: true,
      customFillColor: fillColor,
      customFillColorEnd: fillColorEnd,
    };
  });

  return { labels, datasets };
});

// GK-HUB: Computed chart data with optional goal line dataset for legend
const chartDataWithGoal = computed(() => {
  const base = chartData.value;
  if (props.goalLine == null || !props.goalLabel) return base;
  // Add invisible dataset for goal line legend entry
  return {
    ...base,
    datasets: [
      ...base.datasets,
      {
        label: props.goalLabel,
        data: [],
        borderColor: 'rgba(148, 163, 184, 0.5)',
        borderDash: [6, 4],
        borderWidth: 1.5,
        pointRadius: 0,
        fill: false,
        hidden: false,
      },
    ],
  };
});
// GK-HUB: end goal line legend

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: {
      display: props.showLegend,
      position: 'top',
      align: 'end',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 6,
        boxHeight: 6,
        padding: 16,
        font: {
          family: 'Inter, sans-serif',
          size: 11,
          weight: '500',
        },
        color: '#94a3b8',
        // GK-HUB: Custom legend filter to show goal line with dash style
        generateLabels(chart) {
          return chart.data.datasets.map((ds, i) => {
            const isGoal = ds.borderDash && ds.borderDash.length > 0;
            return {
              text: ds.label,
              fontColor: '#94a3b8',
              fillStyle: isGoal ? 'transparent' : ds.borderColor,
              strokeStyle: ds.borderColor,
              lineWidth: isGoal ? 1.5 : 0,
              lineDash: isGoal ? [6, 4] : [],
              pointStyle: isGoal ? 'line' : 'circle',
              hidden: !chart.isDatasetVisible(i),
              datasetIndex: i,
            };
          });
        },
      },
    },
    tooltip: {
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      titleFont: { family: 'Inter, sans-serif', size: 11, weight: '600' },
      bodyFont: { family: 'Inter, sans-serif', size: 12 },
      titleColor: '#94a3b8',
      bodyColor: '#e2e8f0',
      borderColor: 'rgba(255,255,255,0.08)',
      borderWidth: 1,
      padding: { top: 8, bottom: 8, left: 12, right: 12 },
      boxPadding: 4,
      cornerRadius: 8,
      usePointStyle: true,
      pointStyle: 'circle',
    },
  },
  scales: {
    x: {
      grid: {
        color: 'rgba(148, 163, 184, 0.06)',
        drawBorder: false,
      },
      ticks: {
        font: { family: 'Inter, sans-serif', size: 11 },
        color: '#64748b',
        padding: 8,
        maxRotation: 0,
      },
      border: {
        display: false,
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(148, 163, 184, 0.06)',
        drawBorder: false,
      },
      ticks: {
        font: { family: 'Inter, sans-serif', size: 11 },
        color: '#64748b',
        padding: 8,
        precision: 0,
      },
      border: {
        display: false,
      },
    },
  },
  elements: {
    line: {
      capBezierPoints: true,
    },
  },
  animation: {
    duration: 600,
    easing: 'easeOutQuart',
  },
}));

// Chart.js plugin to create gradient fills
const gradientPlugin = {
  id: 'ispGradientFill',
  beforeDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      if (dataset.fill && dataset.customFillColor) {
        const meta = chart.getDatasetMeta(i);
        if (!meta.dataset) return;
        const yAxis = chart.scales.y;
        const gradient = ctx.createLinearGradient(
          0,
          yAxis.top,
          0,
          yAxis.bottom
        );
        gradient.addColorStop(0, dataset.customFillColor);
        gradient.addColorStop(1, dataset.customFillColorEnd || 'rgba(0,0,0,0)');
        dataset.backgroundColor = gradient;
      }
    });
  },
};

// GK-HUB: Chart.js plugin to draw a dashed horizontal goal line
const goalLinePlugin = {
  id: 'ispGoalLine',
  afterDraw(chart) {
    const goalValue = chart.config.ispGoalLine;
    if (goalValue == null) return;
    const yAxis = chart.scales.y;
    const y = yAxis.getPixelForValue(goalValue);
    if (y == null || y < yAxis.top || y > yAxis.bottom) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.5)';
    ctx.beginPath();
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.stroke();
    ctx.restore();
  },
};
// GK-HUB: end goal line plugin

// GK-HUB: Plugins array with optional goal line
const chartPlugins = computed(() => {
  const plugins = [gradientPlugin];
  if (props.goalLine != null) {
    // Pass goal value via config trick — plugin reads it in afterDraw
    const glPlugin = {
      ...goalLinePlugin,
      beforeInit(chart) {
        chart.config.ispGoalLine = props.goalLine;
      },
    };
    plugins.push(glPlugin);
  }
  return plugins;
});
// GK-HUB: end plugins array
</script>

<template>
  <div class="flex flex-col mb-6">
    <div
      v-if="title"
      class="text-[13px] font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
    >
      {{ title }}
      <span v-if="subtitle" class="font-normal text-n-slate-9 text-xs">{{
        subtitle
      }}</span>
    </div>
    <div class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5">
      <div :style="{ height: height + 'px' }">
        <Line
          :data="chartDataWithGoal"
          :options="chartOptions"
          :plugins="chartPlugins"
        />
      </div>
    </div>
  </div>
</template>
